import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { getCustomerBelowId } from "../actions";
import UserProfile from "./components/UserProfile";
import UserContractsOverview from "./components/UserContractsOverview";
import AbtcAvatar from "../custom/Avatar";
import FixedPageItem from "../custom/FixedPageItem";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem, PageItem } from "../custom/Page";
import { resolveErrorMessage, Roles } from "../util";
import ErrorMessage from "./components/ErrorMessage";
import LoadingSpinner from "./components/LoadingSpinner";
import CustomerManagementActions from "./CustomerManagementActions";

function CustomerDetail({ match, history, location }) {
  const currentUser = useSelector((state) => state.user.user.me);
  const locationState = location?.state;
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(false);

  const contractAgentStatus = currentUser.contractAgent
    ? currentUser.contractAgent.status
    : "NONE";

  const fetchUser = React.useCallback(async () => {
    try {
      const { id } = match.params;
      if (isNaN(id)) throw new Error("app.user.invalidId");
      const userData = await getCustomerBelowId(id);
      setCustomer(userData);
    } catch (e) {
      const errorMessage = resolveErrorMessage(e);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [match.params]);

  useEffect(() => {
    setLoading(true);
    fetchUser();
  }, [fetchUser, match.params.id]);

  const hasBackTo = locationState && Object.keys(locationState).length > 0 && Boolean(locationState?.backTo);

  const handleOnBack = () => {
    hasBackTo ? history.push(locationState?.backTo, locationState?.filter ?? {}) : history.goBack();
  };

  if (loading) return <LoadingSpinner message={<FormattedMessage id="app.loading.fetching.user" />} />;

  if (error)
    return (
      <PageContainer>
        <HeaderPageItem
          title="app.customerInfo"
          onBack={handleOnBack}
        />
        <ErrorMessage>
          <Typography
            variant="h6"
            align="center"
            color="error"
            paragraph
          >
            <FormattedMessage id={error} />
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="textSecondary"
            paragraph
          >
            <FormattedMessage id="app.user.unableToDisplay" />
          </Typography>
        </ErrorMessage>
      </PageContainer>
    );

  return (
    <PageContainer>
      <HeaderPageItem
        title="app.customerInfo"
        onBack={handleOnBack}
      />

      <FixedPageItem
        position="top"
        variant="sticky"
      >
        <Card>
          <CardHeader
            avatar={
              <AbtcAvatar
                imageUrl={customer.photoUrl}
                disableLink
              />
            }
            title={`${customer.name} (${customer.id})`}
            subheader={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flexGrow: 1 }}>
                  {customer.email}{" "}
                  {!customer.apt ? (
                    <span style={{ color: "red" }}>
                      <FormattedMessage id="app.inapt" />
                    </span>
                  ) : (
                    ""
                  )}
                </div>
                <div>
                  {customer.premium && (
                    <>
                      <Typography
                        variant="caption"
                        color="primary"
                      >
                        <FormattedMessage id="app.premium" />
                      </Typography>
                      <br />
                    </>
                  )}
                  <FormattedMessage id={"app.enuns." + customer.role} />
                </div>
              </div>
            }
          />
        </Card>
        <Tabs
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={(event, value) => setTabIndex(value)}
        >
          <Tab label={<FormattedMessage id="app.lccontracts" />} />
          <Tab label={<FormattedMessage id="app.data" />} />
        </Tabs>
      </FixedPageItem>

      <TabContentsGroup value={tabIndex}>
        <TabContents>
          {customer.consultant.id === currentUser.id && [Roles.Customer, Roles.Agent].includes(customer.role) && (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: 100, paddingTop: 12 }}
            >
              {contractAgentStatus === "ACTIVE" ? (
                <div style={{ textAlign: "center" }}>
                  {!customer.apt && (
                    <>
                      <Typography
                        color="primary"
                        variant="body1"
                      >
                        <FormattedHTMLMessage id="app.warning" />
                      </Typography>
                      <Typography
                        variant="body2"
                        gutterBottom
                      >
                        <FormattedHTMLMessage id="app.customer.still.inapt" />
                      </Typography>
                    </>
                  )}
                  {currentUser.taxResidence === customer.taxResidence && (
                    <Button
                      disabled={!customer.apt}
                      variant="contained"
                      color="primary"
                      component={Link}
                      to={"/newContract/" + customer.id}
                    >
                      <FormattedMessage id="app.newContract" />
                    </Button>
                  )}
                </div>
              ) : contractAgentStatus === "PENDING" ? (
                <div style={{ textAlign: "center" }}>
                  <Typography
                    color="primary"
                    variant="body1"
                  >
                    <FormattedHTMLMessage id="app.warning" />
                  </Typography>
                  <Typography variant="body2">
                    <FormattedHTMLMessage id="app.contractAgent.detail.text1" />
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={"/contractAgent/" + currentUser.id}
                    style={{ marginTop: 16 }}
                  >
                    <FormattedHTMLMessage id="app.contractAgent.approve" />
                  </Button>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <Typography
                    color="primary"
                    variant="body1"
                  >
                    <FormattedHTMLMessage id="app.warning" />
                  </Typography>
                  <Typography variant="body2">
                    <FormattedHTMLMessage id="app.contractAgent.detail.text1" />
                    <br />
                    <FormattedHTMLMessage id="app.administrator.contact.please" />
                  </Typography>
                </div>
              )}
            </Grid>
          )}
          <PageItem>
            {currentUser.taxResidence === customer.taxResidence ? (
              <UserContractsOverview
                user={customer}
                showCommissionValue
              />
            ) : (
              <Grid
                container
                align="center"
              >
                <Grid
                  item
                  xs={12}
                >
                  <Typography>
                    <span style={{ color: "red" }}>
                      <FormattedMessage id="app.customer.differentDomicile" />
                    </span>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <Typography>
                    <span style={{ color: "red" }}>
                      <FormattedMessage id="app.customer.cannotOpenAContract" />
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            )}
          </PageItem>
        </TabContents>

        <TabContents>
          <CardPageItem
            raised
            margin={1}
          >
            <CardContent>
              <UserProfile
                currentUser={currentUser}
                user={customer}
              />
            </CardContent>
          </CardPageItem>
          <CustomerManagementActions
            currentUser={currentUser}
            user={customer}
            fetchUser={fetchUser}
          />
        </TabContents>
      </TabContentsGroup>
    </PageContainer>
  );
}

function TabContentsGroup({ children, value }) {
  children = children.filter((c) => c && c.props && !c.props.hidden);
  if (typeof value !== "number" || value < 0 || value >= children.length) return null;
  return children[value];
}

function TabContents({ children }) {
  return children;
}

export default withRouter(CustomerDetail);
